import { UploadCloud } from "lucide-react";
import { useId, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useUploadVersion } from "@/hooks/use-versions";
import { CopyPromptButton } from "./CopyPromptButton";

export function UploadVersionDialog({
  slug,
  solutionName,
  open,
  onOpenChange,
}: {
  slug: string;
  solutionName?: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const fileId = useId();
  const labelId = useId();
  const summaryId = useId();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [label, setLabel] = useState("");
  const [summary, setSummary] = useState("");
  const upload = useUploadVersion(slug);

  const reset = () => {
    setFile(null);
    setLabel("");
    setSummary("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async () => {
    if (!file) return;
    await upload.mutateAsync({
      file,
      label: label.trim() || undefined,
      changeSummary: summary.trim() || undefined,
    });
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload new version</DialogTitle>
          <DialogDescription>
            Drop the architecture markdown generated by Claude Code. It becomes the new current
            version.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="rounded-md border border-dashed border-border bg-muted/30 p-3 flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              Don&apos;t have the file yet? Copy the prompt and paste it into Claude or Cursor.
            </p>
            <CopyPromptButton solutionName={solutionName} size="sm" label="Copy prompt" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={fileId}>Architecture file (.md)</Label>
            <div className="flex items-center gap-3">
              <Input
                id={fileId}
                ref={fileRef}
                type="file"
                accept=".md,text/markdown,text/plain"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </div>
            {file && (
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                <UploadCloud className="h-3.5 w-3.5" />
                {file.name} ({Math.round(file.size / 1024)} KB)
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={labelId}>Label (optional)</Label>
            <Input
              id={labelId}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Post-Entra migration"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={summaryId}>Change summary (optional)</Label>
            <Textarea
              id={summaryId}
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="What changed in this version?"
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!file || upload.isPending}>
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
